// DEV-ONLY internal QA utility for promoting the currently signed-in user
// to staff or admin. Route body is inert (renders 404) in production so it
// cannot be reached on the published site.
//
// To remove before production: delete this file and src/lib/dev-roles.functions.ts.

import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { AuthGate } from "@/components/auth-gate";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { devPromoteSelf } from "@/lib/dev-roles.functions";
import {
  AdminShell,
  AdminPageHeader,
  AdminCard,
  AdminButton,
} from "@/components/admin";

export const Route = createFileRoute("/dev/roles")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Dev — Role Utility" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DevRolesPage,
});

function DevRolesPage() {
  return (
    <AuthGate>
      <DevRolesInner />
    </AuthGate>
  );
}

function DevRolesInner() {
  const { session, roles } = useAuth();
  const promote = useServerFn(devPromoteSelf);
  const [role, setRole] = useState<"customer" | "staff" | "admin">("staff");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const res = await promote({ data: { role } });
      // Force onAuthStateChange to re-fire so useAuth re-fetches roles.
      await supabase.auth.refreshSession().catch(() => undefined);
      const successMsg = `Role updated to "${res.role}". If your session doesn't reflect the change, sign out and back in.`;
      setMsg(successMsg);
      toast.success(successMsg);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to update role";
      setErr(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminShell>
      <div className="mx-auto max-w-2xl">
        <AdminPageHeader
          kicker="Development · QA utility"
          title="Internal role utility"
          description={
            <>
              Promote your signed-in account to <code className="font-mono text-navy">staff</code>{" "}
              or <code className="font-mono text-navy">admin</code> for QA. This
              screen is inert in production builds and is safe to delete before
              launch.
            </>
          }
        />

        <AdminCard>
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-warn">
            <ShieldAlert className="h-3.5 w-3.5" />
            Restricted · Internal
          </div>

          <div className="mt-4 grid gap-2 rounded-lg border border-border bg-secondary/40 p-4 text-xs">
            <div>
              <div className="text-muted-foreground">Signed in as</div>
              <div className="mt-0.5 font-mono text-sm text-navy">
                {session?.user?.email ?? "—"}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Current roles</div>
              <div className="mt-0.5 font-mono text-sm text-navy">
                {roles.length ? roles.join(", ") : "(none)"}
              </div>
            </div>
          </div>

          <form className="mt-6 space-y-4" onSubmit={submit}>
            <label className="block text-sm">
              <span className="font-semibold text-navy">Set role</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as typeof role)}
                className="mt-1 h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-navy/20"
              >
                <option value="customer">customer</option>
                <option value="staff">staff</option>
                <option value="admin">admin</option>
              </select>
            </label>

            <AdminButton
              type="submit"
              loading={busy}
              className="w-full"
            >
              {busy ? "Updating…" : "Update my role"}
            </AdminButton>

            {msg && (
              <div className="rounded-lg bg-savings-soft p-3 text-xs text-savings">
                {msg}
              </div>
            )}
            {err && (
              <div className="rounded-lg bg-destructive/10 p-3 text-xs text-destructive">
                {err}
              </div>
            )}
          </form>
        </AdminCard>
      </div>
    </AdminShell>
  );
}
